/**
 * Attachment file-type detection — maps a `File` (MIME + extension) to a
 * normalized display category so the attachment chip can render the
 * appropriate icon/thumbnail without scattering extension checks across JSX.
 *
 * Shared between the Vite UI (`ChatAttachmentChip`) and compiled tests.
 */

export type AttachmentFileType =
  | "image"
  | "pdf"
  | "json"
  | "text"
  | "markdown"
  | "csv"
  | "word"
  | "excel"
  | "powerpoint"
  | "archive"
  | "generic";

const MIME_MAP: Record<string, AttachmentFileType> = {
  "application/pdf": "pdf",
  "application/json": "json",
  "text/plain": "text",
  "text/markdown": "markdown",
  "text/csv": "csv",
  "application/msword": "word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
  "application/vnd.ms-excel": "excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "excel",
  "application/vnd.ms-powerpoint": "powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "powerpoint",
  "application/zip": "archive",
  "application/x-zip-compressed": "archive",
  "application/x-rar-compressed": "archive",
  "application/vnd.rar": "archive",
  "application/x-7z-compressed": "archive",
  "application/x-tar": "archive",
  "application/gzip": "archive",
  "application/x-gzip": "archive",
};

const EXT_MAP: Record<string, AttachmentFileType> = {
  pdf: "pdf",
  json: "json",
  txt: "text",
  text: "text",
  log: "text",
  md: "markdown",
  markdown: "markdown",
  csv: "csv",
  tsv: "csv",
  doc: "word",
  docx: "word",
  xls: "excel",
  xlsx: "excel",
  ppt: "powerpoint",
  pptx: "powerpoint",
  zip: "archive",
  rar: "archive",
  "7z": "archive",
  tar: "archive",
  gz: "archive",
  tgz: "archive",
};

/** Short uppercase label for the file type badge. */
export const FILE_TYPE_LABELS: Record<AttachmentFileType, string> = {
  image: "IMG",
  pdf: "PDF",
  json: "JSON",
  text: "TXT",
  markdown: "MD",
  csv: "CSV",
  word: "DOC",
  excel: "XLS",
  powerpoint: "PPT",
  archive: "ZIP",
  generic: "FILE",
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Determine the display category for a file. Prefers MIME type when
 * available and falls back to extension matching.
 */
export function getAttachmentFileType(file: { name: string; type?: string }): AttachmentFileType {
  const mime = file.type?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) return "image";
  if (MIME_MAP[mime]) return MIME_MAP[mime];
  const ext = extensionOf(file.name);
  if (EXT_MAP[ext]) return EXT_MAP[ext];
  return "generic";
}

/** Format bytes into a compact human-readable string. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Truncate a filename preserving the extension. For example:
 * `"very-long-filename-with-many-characters.pdf"` → `"very-long-filen….pdf"`
 */
export function truncateFilename(name: string, maxLen = 28): string {
  if (name.length <= maxLen) return name;
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return `${name.slice(0, maxLen - 1)}…`;
  const ext = name.slice(dot); // includes the dot
  const base = name.slice(0, dot);
  const available = maxLen - ext.length - 1; // 1 for the ellipsis
  if (available < 4) return `${name.slice(0, maxLen - 1)}…`;
  return `${base.slice(0, available)}…${ext}`;
}
